import React, {FC, useEffect} from 'react'
import {Trans, useTranslation} from 'react-i18next'
import {FormModelType, useForm} from 'reactjs-use-form'

import ArrowBackSharpIcon from '@mui/icons-material/ArrowBackSharp'
import ArrowForwardSharpIcon from '@mui/icons-material/ArrowForwardSharp'
import {Box, FormControl, Link, MenuItem, TextField} from '@mui/material'
import {
  PGButton,
  PGTypography,
  useProductLine,
} from '@progynyglobal/pg-ui-library'
import {useSnackbar} from 'notistack'

import SubmitButton from '@progyny/components/Buttons/SubmitButton'
import {ResponsiveContainer} from '@progyny/components/ResponsiveContainer'
import {useSubmitFreshdeskTicketMutation} from '@progyny/generated/graphql'
import {useErrorCodes} from '@progyny/hooks/useErrorCodes'
import {useHelpCategories} from '@progyny/hooks/useHelpCategories'
import {useRTL} from '@progyny/hooks/useRTL'
import {RouteEnum, useRoutes} from '@progyny/routes'
import {useMeStore} from '@progyny/store/meStore'

type Props = {closeOverlay?: () => void}
const HelpContact: FC<Props> = ({closeOverlay}) => {
  const companyHasBudget = useMeStore((s) => s.companyHasBudget)
  const {isFertility} = useProductLine()
  const {t} = useTranslation()
  const [request, {loading}] = useSubmitFreshdeskTicketMutation()
  const {enqueueSnackbar} = useSnackbar()
  const getErrorMessage = useErrorCodes()
  const {goTo} = useRoutes()
  const categories = useHelpCategories(t)
  const {isRTL} = useRTL()

  const formModel: FormModelType = {
    message: {
      value: '',
      required: true,
    },
    category: {
      value: '',
      required: true,
    },
  }

  useEffect(() => {
    const handlePopState = () => {
      closeOverlay!()
    }
    window.addEventListener('popstate', handlePopState)
    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const {values, isDisabled, handleOnSubmit, handleOnChange} = useForm(
    formModel,
    handleSubmit
  )

  function goToConsultations() {
    closeOverlay!()
    return goTo(RouteEnum.CONSULTATIONS)
  }

  function handleSubmit() {
    request({
      variables: {
        body: values.message,
        subject: values.category,
      },
    })
      .then((res) => {
        if (res.data?.submitFreshdeskTicket?.success) {
          closeOverlay!()
        } else if (res.data?.submitFreshdeskTicket?.errors) {
          const errors = res.data?.submitFreshdeskTicket?.errors
          const message = errors[0]?.messages[0]

          message &&
            enqueueSnackbar({
              message: getErrorMessage(message),
              variant: 'error',
            })
        }
      })
      .catch((error) => {
        if (error.networkError && error.networkError.statusCode === 429) {
          enqueueSnackbar({
            message: t('too_many_requests'),
            variant: 'warning',
          })
        } else {
          return console.error(error)
        }
      })
  }

  return (
    <Box mt={10}>
      <ResponsiveContainer>
        <Box mx='auto' maxWidth={484}>
          <PGTypography variant='header' as='h1'>
            {t('help_contact_how_can_we_help_you')}
          </PGTypography>
          <Box mt={3}>
            <PGTypography>
              <Trans i18nKey='help_contact_choose_category_below_to_email' />
            </PGTypography>
            {isFertility && (
              <Box mt={2}>
                <Trans
                  i18nKey={'help_contact_do_you_have_question_about'}
                  components={[
                    <Link
                      onClick={goToConsultations}
                      title={t('routes_nav_consultations')}
                      style={{textDecoration: 'underline'}}
                    >
                      {t('link')}
                    </Link>,
                  ]}
                ></Trans>
              </Box>
            )}
          </Box>
          <Box>
            <form onSubmit={handleOnSubmit}>
              <Box mt={2}>
                <FormControl variant={'outlined'} margin={'dense'} fullWidth>
                  <TextField
                    select
                    size='small'
                    name='category'
                    margin={'dense'}
                    label={t('help_contact_category')}
                    placeholder={t('help_contact_category')}
                    value={values.category}
                    id={'category-label'}
                    onChange={handleOnChange}
                  >
                    {categories
                      .filter((item) => !(item.withBudget && !companyHasBudget))
                      .map((reason) => (
                        <MenuItem value={reason.value} key={reason.value}>
                          {reason.label}
                        </MenuItem>
                      ))}
                  </TextField>
                </FormControl>
              </Box>
              <Box mt={2}>
                <TextField
                  name='message'
                  multiline
                  minRows={6}
                  label={t('help_contact_explanation_of_your_issue')}
                  placeholder={t('help_contact_explanation_of_your_issue')}
                  value={values.message}
                  onChange={handleOnChange}
                  fullWidth
                  variant='outlined'
                />
              </Box>
              <Box
                mt={5}
                display={'flex'}
                flexDirection={'row'}
                justifyContent={'flex-end'}
                width={'100%'}
              >
                <Box mr={2}>
                  <PGButton
                    onClick={() => closeOverlay!()}
                    startIcon={
                      isRTL ? <ArrowForwardSharpIcon /> : <ArrowBackSharpIcon />
                    }
                    size={'large'}
                    variant={'outlined'}
                    label={t('common_back')}
                  />
                </Box>
                <SubmitButton
                  type={'submit'}
                  size={'large'}
                  disabled={isDisabled}
                  loading={loading}
                >
                  {t('help_contact_send_message')}
                </SubmitButton>
              </Box>
            </form>
          </Box>
        </Box>
      </ResponsiveContainer>
    </Box>
  )
}
export {HelpContact}
